import { assertTypeEquals, report } from 'report';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { boosterTypeByRewardType, IRewardData } from 'Utility/RewardHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { TActionType } from 'Utility/Tracking/DataSet/Transaction';
import { IPlayerState } from '../IPlayerState';
import { PetsStateHelper } from '../Pets/PetsStateHelper';
import { PlayerStateHelper } from '../PlayerStateHelper';
import { PlayerTask } from '../PlayerTasks';
import { QuestStateHelper } from '../Quests/QuestStateHelper';
import { Quests } from '../QuestType';
import { PopupID } from '../../UI/PopupID';
import { hackForceCurrencyAnimation } from 'Prefabs/UI/HUD/CoinBar';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import IPlayerInventoryData from 'Player/Inventory/IPlayerInventoryData';
import { AnyBoosterType, BoosterType } from 'State/Card/BoosterType';
import { MathHelper } from 'Utility/MathHelper';
import { MAX_EXTRA_CARDS } from 'Constants/Player';
import { track, TrackMethod } from 'Utility/Tracking/Track';
import { BoosterTypeMap } from 'State/Card/BoosterTypeMap';
import { never } from 'Utility/Types';
import { FarmStateHelper } from '../Farm/FarmStateHelper';
import { isFarmItemId } from 'System/Farm/Data/FarmItemData';
import { IGameState } from 'State/Game/IGameState';

export namespace InventoryStateHelper {
    export const receiveRewards = (
        state: IPlayerState,
        rewards: IRewardData[],
        extras: {
            queueTask?: boolean;
            dontTrack?: boolean;
            groupTasks?: boolean;
            queueNext?: boolean;
            rewardFrom?: TActionType;
        } = {}
    ): IPlayerState => {
        let newState = { ...state };
        const oldRewardTasks = newState.tasks;
        rewards.forEach((reward: IRewardData) => {
            newState = giveReward(newState, reward, extras);
        });
        if (extras.groupTasks) {
            newState = {
                ...newState,
                tasks: ArrayHelper.grouped(
                    oldRewardTasks,
                    newState.tasks,
                    (a, b) => a.task === b.task && a.type === b.type,
                    (a, b) => {
                        const result = { ...a };
                        switch (result.task) {
                            case PlayerTask.TNT: // fall through
                            case PlayerTask.BULLS_EYE: // fall through
                            case PlayerTask.JOKER: // fall through
                            case PlayerTask.SHUFFLE: // fall through
                            case PlayerTask.UNDO: // fall through
                            case PlayerTask.FREE_PLAY_REWARD:
                                assertTypeEquals('amount', result.amount, b.amount, 'number');
                                result.amount += b.amount;
                                break;
                            case PlayerTask.EXTRA_CARDS:
                                assertTypeEquals('cards', result.cards, b.cards, 'number');
                                result.cards += b.cards;
                                break;
                            case PlayerTask.COIN:
                                assertTypeEquals('coins', result.coins, b.coins, 'number');
                                assertTypeEquals('previousCoins', result.previousCoins, b.previousCoins, 'number');
                                result.coins += b.coins;
                                result.previousCoins = Math.min(result.previousCoins, b.previousCoins);
                                break;
                            default:
                                report('Unexpected player task', {
                                    once: true,
                                    tags: { taskType: result.task },
                                });
                        }
                        return result;
                    }
                ),
            };
        }
        if (extras.rewardFrom && extras.rewardFrom === TActionType.WHEEL_SPIN) {
            newState = QuestStateHelper.incrementQuestProgressBy(
                newState,
                Quests.SPIN_THE_WHEEL,
                1,
                TActionType.WHEEL_SPIN
            );
        }
        return newState;
    };

    export const getRestrictedBoosters = (state: IPlayerState): RewardType[] => {
        const ret: RewardType[] = [];
        const ftuxToCheck: FTUXType[] = [
            FTUXType.BULLSEYE_BOOSTER,
            FTUXType.SHUFFLE_BOOSTER,
            FTUXType.BOMB_CARD,
            FTUXType.JOKER,
        ];
        const rewardMap: RewardType[] = [RewardType.BULLSEYE, RewardType.SHUFFLE, RewardType.BOMB, RewardType.JOKER];

        for (let i: number = 0; i < ftuxToCheck.length; i++) {
            if (!FTUXStateHelper.isFtuxCompleted(state, ftuxToCheck[i])) {
                ret.push(rewardMap[i]);
            }
        }

        return ret;
    };

    export const addInventory = (
        playerState: IPlayerState,
        itemType: keyof IPlayerInventoryData,
        amount: number,
        queueTask: boolean = false
    ): IPlayerState => {
        return setInventory(playerState, itemType, playerState.inventory[itemType] + amount);
    };

    export const setInventory = (
        playerState: IPlayerState,
        itemType: keyof IPlayerInventoryData,
        amount: number
    ): IPlayerState => {
        return {
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [itemType]: amount,
            },
        };
    };

    export const getInventoryCount = (state: IGameState, itemType: keyof IPlayerInventoryData): number => {
        return state.playerState.inventory[itemType];
    };

    export const consumeInventory = (
        playerState: IPlayerState,
        itemType: keyof IPlayerInventoryData,
        amount: number
    ): IPlayerState => {
        return InventoryStateHelper.setInventory(playerState, itemType, playerState.inventory[itemType] - amount);
    };

    export const giveReward = (
        playerState: IPlayerState,
        reward: IRewardData,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
            forceAnim?: boolean;
        }
    ): IPlayerState => {
        switch (reward.type) {
            case RewardType.BOMB:
                return setBoosterTNT(playerState, playerState.inventory[BoosterType.TNT] + reward.value, extras);
            case RewardType.BULLSEYE:
                return setBoosterBullsEye(
                    playerState,
                    playerState.inventory[BoosterType.BULLS_EYE] + reward.value,
                    extras
                );
            case RewardType.COINS:
                return setCoins(playerState, playerState.inventory.coins + reward.value, extras);
            case RewardType.JOKER:
                return setBoosterJoker(playerState, playerState.inventory[BoosterType.JOKER] + reward.value, extras);
            case RewardType.SHUFFLE:
                return setBoosterShuffle(
                    playerState,
                    playerState.inventory[BoosterType.SHUFFLE] + reward.value,
                    extras
                );
            case RewardType.UNDO:
                return setBoosterUndo(playerState, playerState.inventory[BoosterType.UNDO] + reward.value, extras);
            case RewardType.PRIZE_WHEEL_SPIN:
                return setWheelSpins(playerState, playerState.inventory.wheelSpins + reward.value);
            case RewardType.PLINKO_TOKEN:
                return setPlinkoTokens(playerState, playerState.inventory[RewardType.PLINKO_TOKEN] + reward.value);
            case RewardType.CARDS:
                return setExtraCards(playerState, playerState.inventory[BoosterType.PLUS_ONE] + reward.value, extras);
            case RewardType.FREE_PLAY:
                return setBoosterFreePlay(
                    playerState,
                    playerState.inventory[RewardType.FREE_PLAY] + reward.value,
                    extras
                );
            case RewardType.PLUS_FIVE_CARDS:
                return setBoosterPlusFive(
                    playerState,
                    playerState.inventory[BoosterType.PLUS_FIVE] + reward.value,
                    extras
                );

            case RewardType.PRE_BOOSTER_REMOVE_THREE:
            case RewardType.PRE_BOOSTER_JOKER:
            case RewardType.PRE_BOOSTER_CANISTER:
                const boosterType = boosterTypeByRewardType[reward.type];

                if (!extras.dontTrack) {
                    trackInventory(playerState, boosterType, reward.value);
                }

                return InventoryStateHelper.setAnyBooster(
                    playerState,
                    boosterType,
                    playerState.inventory[boosterType] + reward.value,
                    extras
                );

            case RewardType.PREMIUM_SPIN:
                return setPremiumWheelSpins(playerState, playerState.inventory[RewardType.PREMIUM_SPIN] + reward.value);

            case RewardType.PET:
                let newState = { ...playerState };
                if (!PetsStateHelper.isPetUnlocked(newState.petsState, reward.petId)) {
                    // Unlock the pet
                    newState = {
                        ...newState,
                        petsState: PetsStateHelper.unlockPet(newState.petsState, reward.petId),
                    };
                    // Show reward popup
                    newState = PlayerStateHelper.addStateTaskDatas(newState, [
                        {
                            task: PlayerTask.SHOW_POPUP,
                            popup: PopupID.PET_UNLOCK_MENU,
                            meta: {
                                petId: reward.petId,
                            },
                        },
                    ]);
                    // Queue ftux if needed
                    newState = PetsStateHelper.queuePetFTUXIfNeeded(newState);
                }
                return newState;

            case RewardType.FARM_AREA:
                if (typeof reward.value === 'string') {
                    return {
                        ...playerState,
                        farmState: FarmStateHelper.unlockFarmArea(playerState.farmState, reward.value),
                    };
                }
                return playerState;

            case RewardType.FARM_ITEM:
                if (isFarmItemId(reward.value)) {
                    if (!FarmStateHelper.alreadyHasItem(playerState, reward.value))
                        return FarmStateHelper.rewardNewItem(playerState, reward.value);
                    else if (reward.fallback)
                        return giveReward(playerState, reward.fallback, extras);
                }

                return playerState;

            case RewardType.NONE:
            case RewardType.THREE_STAR_REWARD:
            case RewardType.OTHER:
                return playerState;

            default:
                never(reward.type);
                report('Reward prize type has no logic to give reward', {
                    once: true,
                    tags: { rewardType: reward.type },
                });
                return playerState;
        }
    };

    export const setExtraCards = (
        state: IPlayerState,
        amount: number,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = false } = extras || {};

        if (!dontTrack) {
            trackInventory(state, BoosterType.PLUS_ONE, amount);
        }

        amount = MathHelper.clamp(amount, 0, MAX_EXTRA_CARDS);
        if (amount > state.inventory[BoosterType.PLUS_ONE] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(
                state,
                [
                    {
                        task: PlayerTask.EXTRA_CARDS,
                        cards: amount - state.inventory[BoosterType.PLUS_ONE],
                        queueNext,
                    },
                ],
                queueNext
            );
        }
        return setInventory(state, BoosterType.PLUS_ONE, amount);
    };

    export const setBoosterPlusFive = (
        state: IPlayerState,
        amount: number,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = false } = extras || {};

        if (!dontTrack) {
            trackInventory(state, BoosterType.PLUS_FIVE, amount);
        }

        if (amount > state.inventory[BoosterType.PLUS_FIVE] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(
                state,
                [
                    {
                        task: PlayerTask.EXTRA_CARDS,
                        type: BoosterType.PLUS_FIVE,
                        cards: amount - state.inventory[BoosterType.PLUS_FIVE],
                        queueNext,
                    },
                ],
                queueNext
            );
        }
        return setInventory(state, BoosterType.PLUS_FIVE, amount);
    };

    export const setAnyBooster = (
        state: IPlayerState,
        boosterType: AnyBoosterType,
        amount: number,
        extras?: {
            queueTask?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, queueNext = false } = extras || {};

        if (amount > state.inventory[boosterType] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(
                state,
                [
                    {
                        task: PlayerTask.REWARD_BOOSTER,
                        type: boosterType,
                        amount: amount - state.inventory[boosterType],
                        queueNext,
                    },
                ],
                queueNext
            );
        }

        return {
            ...state,
            inventory: {
                ...state.inventory,
                [boosterType]: amount,
            },
        };
    };

    export const setBoosterFreePlay = (
        state: IPlayerState,
        amount: number,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = false } = extras || {};

        if (!dontTrack) {
            const sum = amount - state.inventory[RewardType.FREE_PLAY];
            if (sum > 0) track('tFreeEntryReceived', sum, TrackMethod.ADD);
            else if (sum < 0) track('tFreeEntryUsed', -sum, TrackMethod.ADD);
        }

        if (amount > state.inventory[RewardType.FREE_PLAY] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(
                state,
                [
                    {
                        task: PlayerTask.FREE_PLAY_REWARD,
                        amount: amount - state.inventory[RewardType.FREE_PLAY],
                        queueNext,
                    },
                ],
                queueNext
            );
        }
        return setInventory(state, RewardType.FREE_PLAY, amount);
    };

    export const setBoosterJoker = (
        state: IPlayerState,
        amount: number,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = false } = extras || {};

        if (!dontTrack) {
            trackInventory(state, BoosterType.JOKER, amount);
        }

        if (amount > state.inventory[BoosterType.JOKER] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(
                state,
                [
                    {
                        task: PlayerTask.JOKER,
                        amount: amount - state.inventory[BoosterType.JOKER],
                        queueNext,
                    },
                ],
                queueNext
            );
        }
        return setInventory(state, BoosterType.JOKER, amount);
    };

    export const setBoosterUndo = (
        state: IPlayerState,
        amount: number,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = false } = extras || {};

        if (!dontTrack) {
            trackInventory(state, BoosterType.UNDO, amount);
        }

        if (amount > state.inventory[BoosterType.UNDO] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(
                state,
                [
                    {
                        task: PlayerTask.UNDO,
                        amount: amount - state.inventory[BoosterType.UNDO],
                        queueNext,
                    },
                ],
                queueNext
            );
        }

        return setInventory(state, BoosterType.UNDO, amount);
    };

    export const setBoosterShuffle = (
        state: IPlayerState,
        amount: number,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = false } = extras || {};

        if (!dontTrack) {
            trackInventory(state, BoosterType.SHUFFLE, amount);
        }

        if (amount > state.inventory[BoosterType.SHUFFLE] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(
                state,
                [
                    {
                        task: PlayerTask.SHUFFLE,
                        amount: amount - state.inventory[BoosterType.SHUFFLE],
                        queueNext,
                    },
                ],
                queueNext
            );
        }

        return setInventory(state, BoosterType.SHUFFLE, amount);
    };

    export const setBoosterBullsEye = (
        state: IPlayerState,
        amount: number,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = false } = extras || {};

        if (!dontTrack) {
            trackInventory(state, BoosterType.BULLS_EYE, amount);
        }

        if (amount > state.inventory[BoosterType.BULLS_EYE] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(
                state,
                [
                    {
                        task: PlayerTask.BULLS_EYE,
                        amount: amount - state.inventory[BoosterType.BULLS_EYE],
                        queueNext,
                    },
                ],
                queueNext
            );
        }

        return setInventory(state, BoosterType.BULLS_EYE, amount);
    };

    export const setBoosterTNT = (
        state: IPlayerState,
        amount: number,
        extras?: {
            queueTask?: boolean;
            dontTrack?: boolean;
            queueNext?: boolean;
        }
    ): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = false } = extras || {};

        if (!dontTrack) {
            trackInventory(state, BoosterType.TNT, amount);
        }
        if (amount > state.inventory[BoosterType.TNT] && queueTask) {
            state = PlayerStateHelper.addStateTaskDatas(state, [
                {
                    task: PlayerTask.TNT,
                    amount: amount - state.inventory.boosterTNT,
                    queueNext,
                },
            ]);
        }

        return setInventory(state, BoosterType.TNT, amount);
    };

    export const setPlinkoTokens = (state: IPlayerState, amount: number): IPlayerState => {
        return setInventory(state, RewardType.PLINKO_TOKEN, amount);
    };

    export const setWheelSpins = (state: IPlayerState, amount: number): IPlayerState => {
        return setInventory(state, 'wheelSpins', amount);
    };

    export const setPremiumWheelSpins = (state: IPlayerState, amount: number): IPlayerState => {
        return setInventory(state, RewardType.PREMIUM_SPIN, amount);
    };

    export interface ISetCoinsExtras {
        queueTask?: boolean;
        dontTrack?: boolean;
        queueNext?: boolean;
        forceAnim?: boolean;
    }

    export const setCoins = (state: IPlayerState, amount: number, extras?: ISetCoinsExtras): IPlayerState => {
        const { queueTask = true, dontTrack = false, queueNext = true, forceAnim = false } = extras || {};

        if (forceAnim) {
            hackForceCurrencyAnimation();
        }

        if (!dontTrack) {
            if (amount < state.inventory.coins)
                track('tCoinsUsed', state.inventory.coins - amount, TrackMethod.ADD);
            else if (amount > state.inventory.coins)
                track('tCoinsReceived', amount - state.inventory.coins, TrackMethod.ADD);
        }
        if (amount > state.inventory.coins && queueTask) {
            const coinTask = state.tasks.find(taskData => taskData.task === PlayerTask.COIN);
            if (coinTask) {
                coinTask.coins += amount - state.inventory.coins;
            } else {
                state = PlayerStateHelper.addStateTaskDatas(
                    state,
                    [
                        {
                            task: PlayerTask.COIN,
                            coins: amount - state.inventory.coins,
                            previousCoins: state.inventory.coins,
                            queueNext,
                        },
                    ],
                    queueNext
                );
            }
        }

        return setInventory(state, 'coins', amount);
    };

    export const spendCoins = (state: IPlayerState, coins: number, extras?: ISetCoinsExtras): IPlayerState => {
        return setCoins(state, state.inventory.coins - coins, extras);
    };

    export const trackInventory = (state: IPlayerState, boosterType: AnyBoosterType, amount: number): void => {
        try {
            if (amount > state.inventory[boosterType]) {
                track(
                    BoosterTypeMap[boosterType].receivedEvent,
                    amount - state.inventory[boosterType],
                    TrackMethod.ADD
                );
            } else if (amount < state.inventory[boosterType]) {
                track(BoosterTypeMap[boosterType].usedEvent, state.inventory[boosterType] - amount, TrackMethod.ADD);
            }
        } catch (e) {
            console.warn('trackInventory failed', e);
        }
    };

    export const setShopGift = (state: IPlayerState, value: number): IPlayerState => {
        return setInventory(state, 'shopGift', value);
    };

    export const hasInInventory = (state: IGameState, itemKey: keyof IPlayerInventoryData): boolean => {
        const itemCount =
            typeof state.playerState.inventory[itemKey] === 'number' ? state.playerState.inventory[itemKey] : 0;
        return itemCount > 0;
    };
}
